import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchBriefing, verifyBriefing } from '../lib/briefingApi.js';
import VoicePlayer from '../components/VoicePlayer.jsx';

// Top-secret aesthetic: classification stamp, classified-memo header,
// audio plays first, body + accept reveal only after the user taps play.
// Spec §12. Audio-primary by design — we don't want recipients reading
// past Wilman's voice.

export default function Briefing() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [phraseRevealed, setPhraseRevealed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchBriefing({ token })
      .then((d) => {
        if (cancelled) return;
        if (d.expired) {
          setError({ message: 'This link has expired. Ask Kishan for a fresh one.' });
          return;
        }
        // Token already consumed — recipient has accepted before. Send
        // them straight to their dashboard. The briefing is a one-shot
        // reveal; replays defeat the purpose.
        if (d.used_at) {
          navigate(d.redirect_after_accept || '/', { replace: true });
          return;
        }
        setData(d);
        // Briefings without an audio render (shouldn't happen post-Phase
        // 4 batch, but defensive): reveal the body immediately.
        if (!d.audio_url) setAudioStarted(true);
      })
      .catch((err) => {
        if (!cancelled) setError({ message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  // Best-effort autoplay attempt once data + audio element exist. iOS
  // Safari will reject without prior user gesture — the prominent
  // "PLAY TRANSMISSION" button below catches that case.
  useEffect(() => {
    if (!data?.audio_url) return;
    const a = audioRef.current;
    if (!a) return;
    a.play().catch(() => {
      // Autoplay blocked — leave the play overlay visible.
    });
  }, [data?.audio_url]);

  async function startAudio() {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
    } catch {
      // Some browsers refuse even after the click — flip state anyway
      // so the user isn't stuck (they can tap the small player below).
      setAudioStarted(true);
    }
  }

  async function accept() {
    if (!data) return;
    setAccepting(true);
    setError(null);
    try {
      const result = await verifyBriefing({ token, phrase: data.needs_phrase ? phrase : undefined });
      navigate(result.redirect || '/wilman', { replace: true });
    } catch (err) {
      setError({
        message:
          err.status === 401
            ? 'Access phrase does not match. Try again.'
            : err.detail?.message || err.message,
      });
    } finally {
      setAccepting(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary p-6">
        <div className="mx-auto max-w-md">
          <ClassificationStamp text="TRANSMISSION ERROR" tone="danger" />
          <div className="card mt-6 px-4 py-6 text-center">
            <p className="font-serif italic text-fg-primary">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-tertiary">
          …decrypting transmission
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary p-6 pb-20">
      <div className="mx-auto max-w-md space-y-6">
        <ClassificationStamp text={data.classification} />

        {/* Memo header is always visible — to / from / re. The body is
            inside the same article but conditionally hidden. */}
        <Memo data={data} bodyVisible={audioStarted} />

        {data.audio_url && !audioStarted && (
          <PlayOverlay onPlay={startAudio} duration={data.audio_duration} />
        )}

        {data.audio_url && (
          <div className={audioStarted ? 'card px-4 py-3' : 'sr-only'} aria-hidden={!audioStarted}>
            <VoicePlayer
              ref={audioRef}
              url={data.audio_url}
              duration={data.audio_duration}
              label="play briefing"
              autoMount
              onStart={() => setAudioStarted(true)}
            />
          </div>
        )}

        {audioStarted && data.needs_phrase && (
          <PhraseBlock
            phrase={phrase}
            setPhrase={setPhrase}
            revealed={phraseRevealed}
            onReveal={() => setPhraseRevealed(true)}
          />
        )}

        {audioStarted && (
          <button
            type="button"
            onClick={accept}
            disabled={accepting || (data.needs_phrase && phrase.trim().length === 0)}
            className="w-full rounded-md border border-action bg-action px-4 py-3 font-mono text-sm uppercase tracking-[0.18em] text-action-fg transition hover:bg-action-hover disabled:opacity-60"
          >
            {accepting ? 'verifying…' : data.accept_label || 'ACCEPT THE ASSIGNMENT'}
          </button>
        )}

        <footer className="pt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          single use · do not forward
        </footer>
      </div>
    </div>
  );
}

function ClassificationStamp({ text, tone }) {
  const isDanger = tone === 'danger';
  return (
    <div
      className={[
        'mx-auto inline-block rotate-[-1.5deg] border-2 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em]',
        isDanger ? 'border-danger/70 text-danger' : 'border-danger/70 text-danger',
      ].join(' ')}
      style={{ display: 'block', textAlign: 'center' }}
    >
      {text}
    </div>
  );
}

function Memo({ data, bodyVisible }) {
  return (
    <article className="card overflow-hidden border-l-4 border-l-copper">
      <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-fg-tertiary">
        <p>internal memo · {data.classification}</p>
        <p>to · {data.memo_to}</p>
        <p>from · {data.memo_from}</p>
        <p>re · {data.memo_re}</p>
        <p>subject · {data.subject}</p>
      </header>
      <div className="space-y-4 px-4 py-5">
        {bodyVisible ? (
          <>
            {data.body.split(/\n\n+/).map((para, i) => (
              <p key={i} className="font-serif text-base leading-relaxed text-fg-primary">
                {para}
              </p>
            ))}
            <p className="font-serif italic text-fg-tertiary">— Mr. Wilman</p>
          </>
        ) : (
          <RedactionBlock />
        )}
      </div>
    </article>
  );
}

// Block of mock-redacted text shown until audio plays. Same approximate
// shape as the real body so the layout doesn't jump when text reveals.
// Solid foreground color (no Tailwind opacity modifier on the var()
// color, which doesn't compose cleanly).
function RedactionBlock() {
  const lines = [16, 24, 19, 22, 14, 20, 18];
  return (
    <div className="space-y-3" aria-hidden>
      {lines.map((w, i) => (
        <div
          key={i}
          className="h-3 rounded-sm"
          style={{
            width: `${(w / 24) * 100}%`,
            backgroundColor: 'var(--fg-primary)',
            opacity: 0.85,
          }}
        />
      ))}
    </div>
  );
}

function PlayOverlay({ onPlay, duration }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      className="flex w-full items-center justify-center gap-3 rounded-md border-2 border-copper bg-copper/10 px-4 py-5 font-mono text-sm uppercase tracking-[0.2em] text-copper-dark transition hover:bg-copper/15"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M7 4l13 8-13 8V4z" />
      </svg>
      <span>play transmission</span>
      {Number.isFinite(duration) && (
        <span className="ml-1 font-mono text-xs tracking-[0.15em] text-copper">
          {Math.round(duration)}s
        </span>
      )}
    </button>
  );
}

function PhraseBlock({ phrase, setPhrase, revealed, onReveal }) {
  return (
    <div className="card px-4 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-tertiary">
        access phrase required
      </p>

      {!revealed ? (
        <button
          type="button"
          onClick={onReveal}
          className="mt-3 block w-full select-none rounded-sm bg-fg-primary px-3 py-2 text-center font-mono text-xs uppercase tracking-[0.2em] text-fg-primary transition hover:bg-fg-primary/95"
          aria-label="Reveal hint"
        >
          ████████████████
        </button>
      ) : (
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-fg-secondary">
          recall the word from your email · case-insensitive
        </p>
      )}

      <input
        type="text"
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder="enter phrase"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
        className="mt-3 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-2 text-base text-fg-primary placeholder:text-fg-tertiary focus:border-action focus:outline-none"
      />
    </div>
  );
}
